import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import Form from 'components/Form';
import Section from 'components/Section';
import AccordionController from 'components/AccordionController';
import TextField from 'components/TextField';
import ServiceTypeSelect from 'components/ServiceTypeSelect';

import { useErrorHandler } from 'lib';
import {
  ServiceEditForm_service$key,
  ServiceType,
} from '__generated__/ServiceEditForm_service.graphql';
import { ServiceEditFormMutation } from '__generated__/ServiceEditFormMutation.graphql';

type FormFields = {
  name: string;
  description: string;
  placeholder: string;
  type: ServiceType | null;
};

type Props = {
  service: ServiceEditForm_service$key;
};

const ServiceEditForm: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { id, name, description, placeholder, type } = useFragment(
    graphql`
      fragment ServiceEditForm_service on Service {
        id
        name
        description
        placeholder
        type
      }
    `,
    props.service
  );

  const [update, updating] = useMutation<ServiceEditFormMutation>(graphql`
    mutation ServiceEditFormMutation($id: ID!, $input: UpdateServiceInput!) {
      updateService(id: $id, input: $input) {
        ...EntitySidebar_entity
        ...ServiceEditForm_service
      }
    }
  `);

  const initial = {
    name,
    description: description ?? '',
    placeholder: placeholder ?? '',
    type,
  };

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: { id, input },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="serviceEditForm">
          <Section id="main" title="Apie">
            <Stack spacing={4}>
              <TextField fullWidth name="name" label="Pavadinimas" />

              <TextField
                fullWidth
                multiline
                name="description"
                label="Aprašymas"
              />

              <TextField fullWidth name="placeholder" label="Placeholder" />
              <ServiceTypeSelect name="type" label="Tipas" />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default ServiceEditForm;
